import ChevronRight from '@mui/icons-material/ChevronRight';
import ReplyIcon from '@mui/icons-material/Reply';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Grid, { GridSize } from '@mui/material/Grid';
import { alpha, styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useErrorContext } from '@qpr/client-library';
import { CardExpansion, ListCard, ListItem, PageHeader } from '@qpr/components';
import React, { useEffect, useState } from 'react';
import { Link, match as Match, Route, Switch } from 'react-router-dom';
import { checkIfExternal } from '../../../utils/url-utils';
import ConditionalLink from './ConditionalLink';
import createNavItemsViewModel, {
  initialState,
  NavItem,
  NavItemsViewModelType
} from './nav-items-view-model';

const PREFIX = 'CardCanvas';

const classes = {
  buttonRoot: `${PREFIX}-buttonRoot`,
  header: `${PREFIX}-header`,
  expansionDescription: `${PREFIX}-expansionDescription`,
  separator: `${PREFIX}-separator`,
  noData: `${PREFIX}-noData`
};

const StyledContainer = styled(Container)(({ theme }) => ({
  [`& .${classes.buttonRoot}`]: {
    margin: theme.spacing(2)
  },

  [`& .${classes.header}`]: {
    paddingBottom: theme.spacing(2)
  },

  [`& .${classes.expansionDescription}`]: {
    color: alpha(theme.palette.grey[900], 0.62)
  },

  [`& .${classes.separator}`]: {
    verticalAlign: 'middle',
    color: theme.palette.primary.main
  },

  [`& .${classes.noData}`]: {
    color: alpha(theme.palette.grey[900], 0.9)
  }
}));

type MapNavItems = (value: NavItem, index: number, array: NavItem[]) => unknown;

export interface CardCanvasProps {
  tenantSymbol?: string;
  path: string[];
  cardCanvasViewModel?: NavItemsViewModelType;
}

const CardCanvas = ({ cardCanvasViewModel, tenantSymbol = '', path }: CardCanvasProps) => {
  const viewModel: NavItemsViewModelType = cardCanvasViewModel || createNavItemsViewModel();

  const [expandedItem, setExpandedItem] = useState({ item: initialState[0], parent: 0 });
  const [data, setData] = useState(initialState);
  const { setError } = useErrorContext();

  useEffect(() => {
    async function initData() {
      setError(null);
      const gotData = await viewModel.getNavItems(path);
      setData(gotData.data);
      if (gotData.error?.message) setError(gotData.error);
    }
    initData();
  }, [tenantSymbol, path]);

  const expand = (item: NavItem, parent: number) => (e: any) => {
    e.preventDefault();
    setExpandedItem({ item, parent });
  };

  let parentId: number;
  let howMany: number;

  const mapItems = (map: MapNavItems) => (navItem: NavItem) => {
    parentId = navItem._id;
    return navItem.children?.length > 0 ? navItem.children.map(map) : null;
  };
  const getCardContent = (subItem: NavItem) => (
    <ConditionalLink
      key={subItem._id || Math.floor(Math.random() * 1000)}
      enabled={subItem.enabled}
      link={subItem.link}
      tenantSymbol={tenantSymbol}
    >
      <ListItem
        title={subItem.title}
        description={subItem.description || ''}
        enabled={subItem.enabled}
        iconOnClick={expand(subItem, parentId)}
        clickable={subItem.link !== undefined && subItem.link !== ''}
      />
    </ConditionalLink>
  );

  const getCards = (navItem: NavItem) => {
    let size: number = 4;
    if (howMany === 1) size = 12;
    if (howMany === 2) size = 6;

    return (
      <Grid
        key={navItem._id}
        item
        xs={12}
        md={howMany === 1 ? 12 : 6}
        lg={size as GridSize}
        data-testid="grid-item"
      >
        <ListCard
          enabled={navItem.enabled}
          tenantSymbol={tenantSymbol}
          title={navItem.title}
          avatar={navItem.icon}
          link={navItem.link}
        >
          <CardExpansion
            title={expandedItem.item.title}
            show={expandedItem.parent === navItem._id}
            onClose={() => setExpandedItem({ item: initialState[0], parent: 0 })}
          >
            <Typography variant="body2" className={classes.expansionDescription}>
              {expandedItem.item.description}
            </Typography>
            <br />
            <div style={{ textAlign: 'center' }}>
              {expandedItem.item.link !== undefined && expandedItem.item.link !== '' ? (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ReplyIcon style={{ transform: 'rotateY(180deg)' }} />}
                  component={checkIfExternal(expandedItem.item.link) ? 'a' : Link}
                  to={`/${tenantSymbol}${expandedItem.item.link}`}
                  href={expandedItem.item.link}
                  onClick={() => setExpandedItem({ item: initialState[0], parent: 0 })}
                >
                  Open
                </Button>
              ) : null}
            </div>
          </CardExpansion>
          <div style={{ visibility: expandedItem.parent !== navItem._id ? 'visible' : 'hidden' }}>
            {mapItems(getCardContent)(navItem) || (
              <Typography variant="subtitle1" className={classes.noData}>
                No data
              </Typography>
            )}
          </div>
        </ListCard>
      </Grid>
    );
  };

  const getRoutes = (navItem: NavItem) => {
    howMany = navItem.children?.length || 0;

    return [
      <Route key={navItem._id} exact path={`*${navItem.link || '/missing'}`}>
        <div className={classes.header}>
          <PageHeader title={navItem.title} icon={navItem.icon} />
        </div>
        <Grid container spacing={1}>
          {mapItems(getCards)(navItem) || (
            <Grid item>
              <Typography variant="h5" className={classes.noData}>
                No data
              </Typography>
            </Grid>
          )}
        </Grid>
      </Route>,
      mapItems(getRoutes)(navItem)
    ];
  };

  const getBreadcrumb = (
    navItem: NavItem,
    match: Match,
    location: any,
    separator = <ChevronRight className={classes.separator} />
  ) => {
    if (match.isExact) {
      return null;
    }

    return (
      <>
        <Chip
          clickable
          component={Link}
          to={`/${tenantSymbol}${navItem.link || '/missing'}`}
          label={navItem.title}
          sx={{ backgroundColor: 'grey.300', '&:hover': { backgroundColor: 'grey.400' } }}
        />
        {navItem.children.map(item => (
          <Switch key={item._id || Math.floor(Math.random() * 1000)}>
            <Route exact path={`*${item.link || '/missing'}`} />
            <Route path={`*${item.link || '/missing'}`}>{separator}</Route>
          </Switch>
        ))}
      </>
    );
  };

  const getBreadcrumbs = (navItem: NavItem) => {
    return [
      <Route
        key={navItem._id || Math.floor(Math.random() * 1000)}
        path={`*${navItem.link || '/missing'}`}
        render={({ match, location }) => getBreadcrumb(navItem, match, location)}
      />,
      mapItems(getBreadcrumbs)(navItem)
    ];
  };

  return (
    <StyledContainer maxWidth="xl" id="GridContainer" data-testid="grid-main">
      <br />
      <Grid container spacing={1}>
        <Grid md={12} item>
          {data.map(getBreadcrumbs)}
        </Grid>
        <Grid md={12} item>
          {data.length > 0 ? (
            <Switch>
              {data.map(getRoutes)}
              <Route>
                <Grid md={12} item>
                  <Typography variant="h5" className={classes.noData}>
                    Page not found
                  </Typography>
                </Grid>
              </Route>
            </Switch>
          ) : (
            <Typography variant="h5" className={classes.noData}>
              No data
            </Typography>
          )}
        </Grid>
      </Grid>
    </StyledContainer>
  );
};
export default CardCanvas;
